// La bozza di «Nuova attività» (tela, artboard 41): un solo stato per tutti i passi, il corpo
// che va al server e la validità di ogni passo. Serve anche a modificarne una: parte dai suoi
// valori.
import type {
  AgentProfile,
  Pipeline,
  Routine,
  RoutineCondition,
  RoutineInput,
  RoutineOptions,
  RoutineSchedule,
  RoutineTrigger,
} from '../../lib/api';
import { DEFAULT_CONDITION, DEFAULT_OPTIONS } from './RoutineExtras';

export const STEPS = [
  'Che attività è',
  'Chi la fa e dove',
  'Il compito',
  'Quando parte',
  'Notifica',
  'Riepilogo e prova',
];

export type Draft = {
  name: string;
  task: string;
  pipeline: string;
  trigger: RoutineTrigger;
  inputHint: string;
  repeat: 'minutes' | 'hours' | 'daily';
  minutes: string;
  hours: string;
  at: string;
  condition: RoutineCondition;
  options: RoutineOptions;
  project: string | null;
};

export function fromRoutine(editing: Routine | undefined, pipelines: Pipeline[]): Draft {
  return {
    name: editing?.name ?? '',
    task: editing?.task ?? '',
    pipeline: editing?.pipeline ?? pipelines[0]?.id ?? 'generic',
    trigger: editing?.trigger ?? 'manual',
    inputHint: editing?.inputHint ?? '',
    repeat: editing?.schedule?.daily_at
      ? 'daily'
      : editing?.schedule?.every_minutes
        ? 'minutes'
        : 'hours',
    minutes: String(editing?.schedule?.every_minutes ?? 15),
    hours: String(editing?.schedule?.every_hours ?? 3),
    at: editing?.schedule?.daily_at ?? '08:00',
    condition: editing?.condition ?? DEFAULT_CONDITION,
    options: { ...DEFAULT_OPTIONS, ...editing?.options },
    project: editing?.project ?? null,
  };
}

export function scheduleOf(d: Draft): RoutineSchedule | null {
  if (d.trigger !== 'schedule') return null;
  if (d.repeat === 'daily') return { daily_at: d.at };
  if (d.repeat === 'minutes') return { every_minutes: Number(d.minutes) };
  return { every_hours: Number(d.hours) };
}

export function bodyOf(d: Draft, enabled: boolean): RoutineInput {
  const operational = d.options.kind === 'operational';
  return {
    name: d.name.trim(),
    task: d.task.trim(),
    pipeline: d.pipeline,
    trigger: d.trigger,
    schedule: scheduleOf(d),
    input_hint: d.trigger === 'input' ? d.inputHint.trim() || null : null,
    enabled,
    project: d.project,
    condition: d.trigger === 'condition' ? d.condition : null,
    // un'Attività la fanno i pop scelti, in ordine; un'operativa il suo pop sul progetto
    options: operational
      ? { ...d.options, profiles: [] }
      : { ...d.options, profile: chainOf(d)[0] ?? null, profiles: chainOf(d) },
  };
}

export const hoursOk = (d: Draft) =>
  Number.isInteger(Number(d.hours)) && Number(d.hours) >= 1 && Number(d.hours) <= 168;
export const minutesOk = (d: Draft) =>
  Number.isInteger(Number(d.minutes)) && Number(d.minutes) >= 1 && Number(d.minutes) <= 1440;
export const timeOk = (d: Draft) => /^([01]\d|2[0-3]):[0-5]\d$/.test(d.at);

/** Un passo è valido quando il server accetterebbe quello che contiene. */
export function validity(d: Draft): boolean[] {
  const operational = d.options.kind === 'operational';
  return [
    Boolean(d.pipeline),
    // un'Attività la fanno i pop scelti, da soli; un'operativa vuole anche il progetto
    (operational ? Boolean(d.options.profile) : chainOf(d).length > 0) &&
      (!operational || Boolean(d.project)),
    Boolean(d.name.trim() && d.task.trim()),
    (d.trigger !== 'schedule' ||
      (d.repeat === 'daily' ? timeOk(d) : d.repeat === 'minutes' ? minutesOk(d) : hoursOk(d))) &&
      (d.trigger !== 'condition' || conditionOk(d)),
    (d.options.notify ?? []).every((n) => n.recipients.length > 0),
    true,
  ];
}

/** «Quando succede»: memo vuole spazio e progetto, Gmail la connessione (e l'etichetta, se la
 * condizione guarda le email con un'etichetta). */
export function conditionOk(d: Draft): boolean {
  const c = d.condition;
  if (c.source === 'gmail') return Boolean(c.connection) && (c.label == null || !!c.label.trim());
  if (c.source === 'api') return Boolean(c.connection && c.field?.trim() && c.target?.trim());
  if (c.source === 'calendar') return Boolean(c.connection);
  if (c.scope === 'all') return true;
  if (c.scope === 'space') return Boolean(c.space?.trim());
  return Boolean(c.watch || (c.space?.trim() && c.project?.trim()));
}

/** I pop di un'Attività, in ordine: si passano il compito senza PM. */
export function chainOf(d: Draft): string[] {
  if (d.options.kind === 'operational') return [];
  return d.options.profiles?.length
    ? d.options.profiles
    : d.options.profile
      ? [d.options.profile]
      : [];
}

/** Il pop che lavora (in un'Attività il primo della catena; il PM non c'entra). */
export function workerOf(d: Draft, profiles: AgentProfile[]): AgentProfile | undefined {
  const chosen = profiles.find((p) => p.id === d.options.profile);
  return chosen?.coordinator ? undefined : chosen;
}
